import { Component, OnInit, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { FormsModule, NgForm } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import {
	ItIconComponent,
	ItTabContainerComponent,
	ItTabItemComponent,
	ItModalComponent,
	ItBreadcrumbComponent,
	ItBreadcrumbItemComponent,
	ItButtonDirective,
	ItInputComponent,
} from 'design-angular-kit';
import { TranslateModule } from '@ngx-translate/core';
import { IterlexService } from '../iterlex.service';
import { PhaseComponent } from '../phase/phase.component';
import { DossierGraphComponent } from '../dossier-graph/dossier-graph.component';
import { NotificationsComponent } from '../notifications/notifications.component';
import type { Authority } from '../interfaces/authority';
import type { Dossier } from '../interfaces/dossier';
import type { DossierType } from '../interfaces/dossier-type';
import type { Phase } from '../interfaces/phase';
import type { DossierExpression } from '../interfaces/dossier-expression';

@Component({
	selector: 'app-dossier-page',
	imports: [
		FormsModule,
		ItIconComponent,
		ItTabContainerComponent,
		ItTabItemComponent,
		ItModalComponent,
		ItBreadcrumbComponent,
		ItBreadcrumbItemComponent,
		ItButtonDirective,
		ItInputComponent,
		PhaseComponent,
		DossierGraphComponent,
		NotificationsComponent,
		TranslateModule,
		RouterLink,
		DatePipe,
	],
	templateUrl: './dossier-page.component.html',
	styleUrl: './dossier-page.component.scss',
})
export class DossierPageComponent implements OnInit {
	private router = inject(Router);
	private activatedRoute = inject(ActivatedRoute);
	private iterlexService = inject(IterlexService);

	authority?: Authority;
	body?: Authority;
	tabPrefix = 'phase-';
	dossier: Dossier | null = null;
	phases!: Phase[];
	dossierExpressions: DossierExpression[] = [];
	dossierType?: DossierType;
	currentPhaseId = '';
	ngOnInit(): void {
		const dossierUri =
			this.activatedRoute.snapshot.queryParamMap.get('uri');
		if (dossierUri) {
			const dossierAuthor = dossierUri.split('/')[5];
			this.iterlexService.getAuthorities().subscribe((authorities) => {
				for (const authority of authorities) {
					if (authority.id == dossierAuthor) {
						this.body = authority;
						this.authority = authority;
						break;
					} else if (authority.children.length > 0) {
						this.body = authority.children.find(
							(authority) => authority.id == dossierAuthor,
						);
						if (this.body) {
							this.authority = authority;
							break;
						}
					}
				}
				if (this.authority && this.body) {
					this.iterlexService
						.getDossier(this.authority.id, this.body.id, dossierUri)
						.subscribe((dossier) => {
							this.dossier = dossier;
							this.iterlexService
								.getDossierTypes(
									new HttpParams()
										.append('authority', this.authority!.id)
										.append('body', this.body!.id),
								)
								.subscribe((dossierTypes) => {
									this.dossierType = dossierTypes.find(
										(e) => e.id == dossier.type,
									);
								});
							this.iterlexService
								.getPhases(
									this.authority!.id,
									this.body!.id,
									dossier.type,
								)
								.subscribe((phases) => {
									this.phases = phases;
									const phaseQueryParam =
										this.activatedRoute.snapshot.queryParamMap.get(
											'phase',
										);
									if (phaseQueryParam) {
										this.currentPhaseId = phaseQueryParam;
									}
									this.iterlexService
										.getDossierExpressions(
											this.authority!.id,
											this.body!.id,
											dossier.workUri,
										)
										.subscribe((dossierExpressions) => {
											this.dossierExpressions =
												dossierExpressions;
											const currentDossierPhase =
												dossierExpressions.at(
													-1,
												)?.phaseId;
											document
												.getElementById(
													this.tabPrefix +
														currentDossierPhase +
														'-tab-link',
												)
												?.classList.add(
													'current-phase',
												);
											if (!this.currentPhaseId) {
												this.currentPhaseId = String(
													currentDossierPhase ??
														phases[0].id,
												);
											}
										});
								});
						});
				}
			});
		}
	}
	changeTab(tab: ItTabItemComponent) {
		this.currentPhaseId = tab.id.replace(this.tabPrefix, '');
		this.router.navigate([], {
			queryParams: { phase: this.currentPhaseId, step: null },
			queryParamsHandling: 'merge',
		});
	}
	onEditDossierSubmit(form: NgForm) {
		if (form.valid) {
			this.iterlexService
				.editDossier(
					this.dossier!.workUri,
					this.authority!.id,
					this.body!.id,
					form.value.title,
				)
				.subscribe(() => {
					this.dossier!.title = form.value.title;
				});
		}
	}
	goToDossierStats() {
		this.router.navigate(['/dossier-stats'], {
			queryParams: { uri: this.dossier?.workUri },
		});
	}
	refreshDossier() {
		this.iterlexService
			.getDossier(
				this.authority!.id,
				this.body!.id,
				this.dossier!.workUri,
			)
			.subscribe((dossier) => {
				this.dossier = dossier;
			});
	}
	markDossierAsArchived() {
		this.iterlexService
			.editDossier(
				this.dossier!.workUri,
				this.authority!.id,
				this.body!.id,
				this.dossier!.title,
				'archived',
			)
			.subscribe(() => {
				this.refreshDossier();
			});
	}
}
